import React, { useState, useMemo } from 'react';
import { Calculator, DollarSign, Globe, Briefcase, Plane } from 'lucide-react';

/**
 * Services Step - One-time and recurring professional services
 * 
 * ⭐ M11: Added blended rate calculator for onshore/offshore mix
 * ⭐ M14: Removed Technical Support Setup (no SKU)
 * Added: Managed Services, Offshore development, Travel expenses
 */
const ServicesStep = ({ quoteData, setQuoteData, pricing }) => {
  // ⭐ M11: Blended rate state
  const [blendedHours, setBlendedHours] = useState(quoteData.blendedHours || 0);
  const [onshorePercent, setOnshorePercent] = useState(quoteData.onshorePercent || 30);
  
  const setServiceQuantity = (service, quantity) => {
    setQuoteData({
      ...quoteData,
      services: {
        ...quoteData.services,
        [service]: parseInt(quantity) || 0
      }
    });
  };

  // ⭐ M11: Calculate blended rate
  const blendedCalculation = useMemo(() => {
    const onshoreRate = pricing.blendedRates?.onshoreRate || 200;
    const offshoreRate = pricing.blendedRates?.offshoreRate || 60;
    
    const onshoreHours = Math.round(blendedHours * (onshorePercent / 100));
    const offshoreHours = blendedHours - onshoreHours;
    
    const onshoreCost = onshoreHours * onshoreRate;
    const offshoreCost = offshoreHours * offshoreRate;
    const totalCost = onshoreCost + offshoreCost;
    const effectiveRate = blendedHours > 0 ? totalCost / blendedHours : 0;
    
    return {
      onshoreHours,
      offshoreHours,
      onshoreCost,
      offshoreCost,
      totalCost,
      effectiveRate: effectiveRate.toFixed(2)
    };
  }, [blendedHours, onshorePercent, pricing.blendedRates]);

  // ⭐ M11: Apply blended rate to quote
  const applyBlendedRate = () => {
    const { onshoreHours, offshoreHours } = blendedCalculation;
    
    setQuoteData({
      ...quoteData,
      blendedHours,
      onshorePercent,
      services: {
        ...quoteData.services,
        'Custom Development - Onshore (per hour)': onshoreHours,
        'Custom Development - Offshore (per hour)': offshoreHours
      }
    });
  };

  // Group services by type
  const serviceGroups = useMemo(() => {
    const groups = {
      implementation: [],
      training: [],
      development: [],
      managed: [],
      other: []
    };
    
    Object.entries(pricing.services).forEach(([name, info]) => {
      const item = { name, ...info };
      if (name.includes('Implementation')) groups.implementation.push(item);
      else if (name.includes('Training')) groups.training.push(item);
      else if (name.includes('Development')) groups.development.push(item);
      else if (name.includes('Managed')) groups.managed.push(item);
      else groups.other.push(item);
    });
    
    return groups;
  }, [pricing.services]);

  // Calculate totals
  const serviceTotals = useMemo(() => {
    let oneTime = 0;
    let recurring = 0;
    
    Object.entries(quoteData.services || {}).forEach(([service, qty]) => {
      if (qty > 0 && pricing.services[service]) {
        const info = pricing.services[service];
        const amount = info.price * qty;
        if (info.isRecurring) {
          recurring += amount;
        } else {
          oneTime += amount;
        }
      }
    });
    
    return { oneTime, recurring };
  }, [quoteData.services, pricing.services]);

  const ServiceCard = ({ service }) => {
    const isVariable = service.isVariable;
    const isRecurring = service.isRecurring;
    const quantity = quoteData.services?.[service.name] || 0;
    
    return (
      <div className={`p-5 rounded-xl border-2 transition-all ${
        quantity > 0 
          ? 'border-blue-500 bg-blue-50' 
          : 'border-slate-200 bg-slate-50'
      }`}>
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <h4 className="font-semibold text-slate-800">{service.name}</h4>
            <p className="text-xs text-slate-400 mt-1">SKU: {service.sku}</p>
            {isRecurring && (
              <span className="inline-block mt-2 text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">
                Recurring Monthly
              </span>
            )}
          </div>
          <div className="text-right">
            <div className="text-lg font-bold text-blue-600">
              {isVariable ? 'Variable' : `$${service.price.toLocaleString()}`}
            </div>
            <div className="text-xs text-slate-500">
              {isRecurring ? '/month' : 'one-time'}
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <label className="text-sm text-slate-600">
            {isVariable ? 'Amount ($):' : 'Quantity:'}
          </label>
          <input
            type="number"
            min="0"
            value={quantity || ''}
            onChange={(e) => setServiceQuantity(service.name, e.target.value)}
            className="flex-1 p-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="0"
          />
        </div>
        
        {quantity > 0 && (
          <div className="mt-3 pt-3 border-t border-slate-200 text-right">
            <span className="text-sm text-slate-600">Subtotal: </span>
            <span className="font-bold text-slate-800">
              ${(service.price * quantity).toLocaleString()}
            </span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-slate-800 mb-2">Professional Services</h2>
      <p className="text-slate-600 mb-8">Configure implementation, training, and ongoing services</p>
      
      <div className="space-y-8">
        {/* ⭐ M11: Blended Rate Calculator */}
        <div className="p-6 bg-gradient-to-r from-purple-50 to-blue-50 border-2 border-purple-200 rounded-xl">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center">
              <Calculator size={20} className="text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-purple-900">Blended Rate Calculator</h3>
              <p className="text-sm text-purple-700">Calculate onshore/offshore development mix</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Total Hours
              </label>
              <input
                type="number"
                min="0"
                value={blendedHours || ''}
                onChange={(e) => setBlendedHours(parseInt(e.target.value) || 0)}
                className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                placeholder="e.g., 200"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Onshore % ({onshorePercent}%)
              </label>
              <input
                type="range"
                min="0"
                max="100"
                value={onshorePercent}
                onChange={(e) => setOnshorePercent(parseInt(e.target.value))}
                className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer"
              />
              <div className="flex justify-between text-xs text-slate-500 mt-1">
                <span>100% Offshore</span>
                <span>100% Onshore</span>
              </div>
            </div>
            
            <div className="bg-white rounded-lg p-4 border border-purple-200">
              <div className="text-sm text-slate-600 mb-1">Effective Blended Rate</div>
              <div className="text-2xl font-bold text-purple-600">
                ${blendedCalculation.effectiveRate}/hr
              </div>
            </div>
          </div>
          
          {blendedHours > 0 && (
            <div className="bg-white rounded-lg p-4 border border-purple-200 mb-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-slate-600">Onshore Hours:</span>
                  <div className="font-bold">{blendedCalculation.onshoreHours} hrs @ $200</div>
                  <div className="text-blue-600">${blendedCalculation.onshoreCost.toLocaleString()}</div>
                </div>
                <div>
                  <span className="text-slate-600">Offshore Hours:</span>
                  <div className="font-bold">{blendedCalculation.offshoreHours} hrs @ $60</div>
                  <div className="text-green-600">${blendedCalculation.offshoreCost.toLocaleString()}</div>
                </div>
                <div>
                  <span className="text-slate-600">Total Hours:</span>
                  <div className="font-bold">{blendedHours} hrs</div>
                </div>
                <div>
                  <span className="text-slate-600">Total Cost:</span>
                  <div className="text-xl font-bold text-purple-600">
                    ${blendedCalculation.totalCost.toLocaleString()}
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <button
            onClick={applyBlendedRate}
            disabled={blendedHours === 0}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              blendedHours > 0
                ? 'bg-purple-600 text-white hover:bg-purple-700'
                : 'bg-slate-200 text-slate-400 cursor-not-allowed'
            }`}
          >
            Apply to Quote
          </button>
        </div>

        {/* Implementation Services */}
        {serviceGroups.implementation.length > 0 && (
          <div>
            <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <Briefcase size={18} className="text-blue-600" />
              Implementation
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {serviceGroups.implementation.map(service => (
                <ServiceCard key={service.name} service={service} />
              ))}
            </div>
          </div>
        )}

        {/* Training Services */}
        {serviceGroups.training.length > 0 && (
          <div>
            <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <Globe size={18} className="text-green-600" />
              Training
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {serviceGroups.training.map(service => (
                <ServiceCard key={service.name} service={service} />
              ))}
            </div>
          </div>
        )}

        {/* Development Services */}
        {serviceGroups.development.length > 0 && (
          <div>
            <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <DollarSign size={18} className="text-purple-600" />
              Custom Development
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {serviceGroups.development.map(service => (
                <ServiceCard key={service.name} service={service} />
              ))}
            </div>
          </div>
        )}

        {/* Managed Services */}
        {serviceGroups.managed.length > 0 && (
          <div>
            <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <Globe size={18} className="text-orange-600" />
              Managed Services
              <span className="text-xs font-normal text-orange-600 bg-orange-50 px-2 py-1 rounded-full">
                Recurring
              </span>
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {serviceGroups.managed.map(service => (
                <ServiceCard key={service.name} service={service} />
              ))}
            </div>
          </div>
        )}

        {/* Other Services (Migration, Travel) */}
        {serviceGroups.other.length > 0 && (
          <div>
            <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <Plane size={18} className="text-slate-600" />
              Other Services
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {serviceGroups.other.map(service => (
                <ServiceCard key={service.name} service={service} />
              ))}
            </div>
          </div>
        )}

        {/* Services Summary */}
        <div className="p-6 bg-slate-100 rounded-xl border border-slate-200">
          <h3 className="font-semibold text-slate-800 mb-4">Services Summary</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg p-4 border border-slate-200">
              <div className="text-sm text-slate-600 mb-1">One-Time Services</div>
              <div className="text-2xl font-bold text-blue-600">
                ${serviceTotals.oneTime.toLocaleString()}
              </div>
            </div>
            <div className="bg-white rounded-lg p-4 border border-slate-200">
              <div className="text-sm text-slate-600 mb-1">Recurring Monthly</div>
              <div className="text-2xl font-bold text-purple-600">
                ${serviceTotals.recurring.toLocaleString()}/mo
              </div>
              {serviceTotals.recurring > 0 && (
                <div className="text-xs text-slate-500 mt-1">
                  (${(serviceTotals.recurring * 12).toLocaleString()}/year)
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServicesStep;
